// Brand typefaces (TeX Gyre Adventor + Droid Serif) are self-hosted and
// declared with @font-face in src/css/style.css.

// normalize CSS across browsers
import "./src/css/normalize.css"

// custom CSS styles (brand tokens + base)
import "./src/css/style.css"

import { trackPageView } from "./src/utils/analytics"

import { LEGACY_HASH_ALIASES } from "./src/legacyHashAliases"

/**
 * Rewrite an inbound legacy in-page anchor to its new section id.
 *
 * The old dingwellfh.ca used underscored ids (#immediate_need) and, in two cases,
 * an id unrelated to the heading (#about_us was the "Located in Souris" band).
 * A server redirect cannot fix these — the fragment never reaches the server —
 * so inbound links from the site this one replaces land on a page and scroll
 * nowhere. See src/legacyHashAliases.js for the map and its rationale.
 *
 * This lives in gatsby-browser.js, NOT wrapRootElement.js: the latter is
 * re-exported from BOTH gatsby-browser and gatsby-ssr precisely so SSR and
 * hydration render identically, and location.hash does not exist during SSR.
 * See DECISIONS.md [2026-08-24] REVIEW PATTERN.
 */
const applyLegacyHashAlias = () => {
  if (typeof window === "undefined") return
  const raw = (window.location.hash || "").replace(/^#/, "")
  if (!raw) return
  const target = LEGACY_HASH_ALIASES[raw]
  if (!target) return
  // Only rewrite when the OLD id is absent, so a future section legitimately
  // named e.g. "contact" is never hijacked by the alias.
  if (document.getElementById(raw)) return

  // Sections mount with the page, and on a cold load the target does not exist
  // yet when the router first reports the route. Poll briefly rather than
  // guessing a single frame — one rAF was measurably too early.
  const deadline = Date.now() + 2000
  const attempt = () => {
    const el = document.getElementById(target)
    if (!el) {
      if (Date.now() < deadline) window.requestAnimationFrame(attempt)
      return
    }
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}#${target}`
    )
    el.scrollIntoView()
  }
  attempt()
}

// Cold load: the router does not emit a route update for the first render.
export const onInitialClientRender = () => applyLegacyHashAlias()

// GA4 (gtag.js) is loaded ONCE by gatsby-ssr.js into the static <head>; that inline snippet
// runs before hydration, so window.gtag + dataLayer and the initial config already exist by the
// time the client boots. onClientEntry used to append a SECOND gtag script and re-run config
// here — removed, so the library loads once. onRouteUpdate sends SPA page_views (config sets
// send_page_view:false); the send itself lives in src/utils/analytics.js, which owns every
// window.gtag call and defers the event until the new <title> has committed.

export const onRouteUpdate = ({ location }) => {
  applyLegacyHashAlias()
  trackPageView(location)
}

// Wrap every page in the funeral-ui provider (shared with gatsby-ssr.js).
export { wrapRootElement } from "./src/wrapRootElement"
